/* Static, theme-owned translations. Currency changes use Shopify's localization form. */
(() => {
  const languages = {en:'English',ru:'Русский',pl:'Polski',it:'Italiano',ro:'Română',es:'Español',de:'Deutsch',fr:'Français',tr:'Türkçe',ja:'日本語',zh:'中文',ko:'한국어'};
  const currencies = {USD:['$','US Dollar'],EUR:['€','Euro'],GBP:['£','British Pound'],TRY:['₺','Turkish Lira'],PLN:['zł','Polish Złoty'],RUB:['₽','Russian Ruble'],JPY:['¥','Japanese Yen'],CNY:['CN¥','Chinese Yuan'],KRW:['₩','South Korean Won'],BRL:['R$','Brazilian Real'],CAD:['CA$','Canadian Dollar'],AUD:['A$','Australian Dollar'],RON:['lei','Romanian Leu'],INR:['₹','Indian Rupee']};
  const storage = {get(key){try{return localStorage.getItem(key);}catch{return null;}},set(key,value){try{localStorage.setItem(key,value);}catch{}}};
  const normalize = text => text.replace(/\s+/g,' ').trim();
  const originalTexts = new WeakMap(), originalAttributes = new WeakMap();
  const dictionaries = {en:{}}, pendingLoads = {};
  const sourceByTranslation = new Map();
  let activeLanguage = 'en', dictionary = {}, observer, rendering = false;
  const excluded = 'script,style,noscript,svg,textarea,[contenteditable],[data-no-translate],.cosmo-product-tile,.cosmo-status-card h2,.cv10__revName,.divine-member,.divine-payment-band';
  function translateDOM(root = document.body) {
    if (rendering) return;
    rendering = true;
    observer?.disconnect();
    const walker = document.createTreeWalker(root,NodeFilter.SHOW_TEXT);
    let node;
    while ((node = walker.nextNode())) {
      if (!node.parentElement || node.parentElement.closest(excluded)) continue;
      const saved = originalTexts.get(node);
      // External components may replace counter text without replacing its node.
      let original = saved && (node.data === saved.output || node.data === saved.input) ? saved.input : node.data;
      // Resized carousels clone rendered DOM. Recover the source for those new nodes
      // so switching language again cannot leave the clones in an older language.
      if (!saved && sourceByTranslation.has(normalize(original))) {
        original = original.replace(/\S[\s\S]*\S|\S/,sourceByTranslation.get(normalize(original)));
      }
      const key = normalize(original);
      const translated = dictionary[key];
      const output = translated ? original.replace(/\S[\s\S]*\S|\S/,translated) : original;
      originalTexts.set(node,{input:original,output});
      if (node.data !== output) node.data = output;
    }
    for (const element of root.querySelectorAll('[placeholder],[aria-label],[title]')) {
      if (element.closest(excluded)) continue;
      const saved = originalAttributes.get(element) || {};
      for (const attribute of ['placeholder','aria-label','title']) {
        const value = element.getAttribute(attribute);
        if (value === null) continue;
        const prior = saved[attribute];
        const input = prior && (value === prior.output || value === prior.input) ? prior.input : value;
        const output = dictionary[normalize(input)] || input;
        saved[attribute] = {input,output};
        if (value !== output) element.setAttribute(attribute,output);
      }
      originalAttributes.set(element,saved);
    }
    observer?.observe(document.body,{childList:true,subtree:true,characterData:true});
    rendering = false;
  }
  async function loadDictionary(language,element) {
    if (dictionaries[language]) return dictionaries[language];
    if (!pendingLoads[language]) pendingLoads[language] = (async () => {
      const url = element.dataset.dictionaryUrl.replace('divine-translations-ru.json',`divine-translations-${language}.json`);
      const response = await fetch(url,{credentials:'same-origin'});
      if (!response.ok) throw new Error('Translation unavailable');
      const data = await response.json();
      dictionaries[language] = data;
      Object.entries(data).forEach(([source,translation])=>sourceByTranslation.set(normalize(translation),source));
      return data;
    })().finally(() => {delete pendingLoads[language];});
    return pendingLoads[language];
  }
  async function applyLanguage(language,element) {
    const data = await loadDictionary(language,element);
    activeLanguage = language;
    dictionary = data;
    document.documentElement.lang = language;
    translateDOM();
    document.dispatchEvent(new CustomEvent('divine:language-change',{detail:{language}}));
  }
  function initialize(element) {
    if (element.dataset.initialized) return;
    element.dataset.initialized = 'true';
    const preview = document.body.dataset.localPreview === 'true';
    const nativeLanguage = element.dataset.language || 'en';
    let currentLanguage = storage.get('divine-language') || nativeLanguage.split('-')[0];
    if (!languages[currentLanguage]) currentLanguage = 'en';
    const markets = [...element.querySelectorAll('[data-market-country]')].map(x=>({country:x.dataset.marketCountry,currency:x.dataset.marketCurrency,symbol:x.dataset.marketSymbol}));
    const availableCurrencies = [...new Set(markets.map(x=>x.currency))];
    const nativeLanguages = [...element.querySelectorAll('[data-native-language]')].map(x=>x.dataset.nativeLanguage);
    let currentCurrency = preview ? storage.get('divine-currency') || element.dataset.currency : element.dataset.currency;
    if (!availableCurrencies.includes(currentCurrency)) currentCurrency = element.dataset.currency;
    const dialog = element.querySelector('dialog');
    const openButton = element.querySelector('[data-locale-open]');
    const saveButton = element.querySelector('[data-locale-save]');
    let draft = {language:currentLanguage,currency:currentCurrency};
    function currencyInfo(code) {return currencies[code] || [markets.find(x=>x.currency===code)?.symbol || code,code];}
    function optionContent(type,code) {
      const fragment = document.createDocumentFragment();
      const mark = document.createElement('span');
      mark.setAttribute('aria-hidden','true');
      mark.className = type === 'language' ? `divine-flag divine-flag--${code}` : 'divine-currency-mark';
      if (type === 'currency') mark.textContent = currencyInfo(code)[0];
      fragment.append(mark,document.createTextNode(type === 'language' ? languages[code] : `${code} · ${currencyInfo(code)[1]}`));
      return fragment;
    }
    function updateHeader() {
      element.querySelector('[data-current-language]').textContent = languages[currentLanguage];
      element.querySelector('[data-current-flag]').className = `divine-flag divine-flag--${currentLanguage}`;
      element.querySelector('[data-current-currency]').textContent = currentCurrency;
      element.querySelector('[data-current-symbol]').textContent = currencyInfo(currentCurrency)[0];
    }
    const selects = [...element.querySelectorAll('[data-select]')];
    function closeLists() {
      selects.forEach(select=>{
        select.querySelector('.divine-locale-dropdown').hidden = true;
        select.querySelector('button').setAttribute('aria-expanded','false');
      });
    }
    function updateDraft() {
      for (const select of selects) {
        const type = select.dataset.select;
        select.querySelector('[data-selection]').replaceChildren(optionContent(type,draft[type]));
        select.querySelectorAll('[role=option]').forEach(option=>option.setAttribute('aria-selected',String(option.dataset.value===draft[type])));
      }
    }
    for (const select of selects) {
      const type = select.dataset.select;
      const trigger = select.querySelector('button');
      const list = select.querySelector('[role=listbox]');
      list.replaceChildren();
      const dropdown = select.querySelector('.divine-locale-dropdown');
      const search = select.querySelector('input');
      const codes = type === 'language' ? Object.keys(languages) : [...Object.keys(currencies),...availableCurrencies.filter(code=>!currencies[code])];
      for (const code of codes) {
        const option = document.createElement('button');
        option.type = 'button';option.className = 'divine-locale-option';
        option.setAttribute('role','option');option.setAttribute('data-no-translate','');
        option.dataset.value = code;
        option.append(optionContent(type,code));
        if (type === 'currency' && !availableCurrencies.includes(code)) {
          option.disabled = true;
          option.setAttribute('aria-disabled', 'true');
          const label = document.createElement('small');
          label.textContent = 'Unavailable'; label.className = 'divine-currency-unavailable';
          option.append(label);
        }
        option.addEventListener('click',()=>{draft[type]=code;updateDraft();closeLists();trigger.focus();});
        list.append(option);
      }
      function filter() {
        const query = search.value.trim().toLocaleLowerCase();
        let visible = 0;
        list.querySelectorAll('button').forEach(option=>{
          option.hidden = !`${option.dataset.value} ${option.textContent}`.toLocaleLowerCase().includes(query);
          if (!option.hidden) visible++;
        });
        select.querySelector('[data-no-results]').hidden = visible > 0;
      }
      trigger.addEventListener('click',()=>{
        const wasOpen = !dropdown.hidden;
        closeLists();
        if (!wasOpen) {
          dropdown.hidden = false;trigger.setAttribute('aria-expanded','true');search.value='';filter();search.focus();
          const availableSpace = window.innerHeight - dropdown.getBoundingClientRect().top - 16;
          list.style.maxHeight = `${Math.max(90,Math.min(245,availableSpace-52))}px`;
        }
      });
      search.addEventListener('input',filter);
      dropdown.addEventListener('keydown',event=>{
        const options = [...list.querySelectorAll('button:not([hidden]):not(:disabled)')];
        const index = options.indexOf(document.activeElement);
        if (event.key === 'Escape') {event.preventDefault();event.stopPropagation();closeLists();trigger.focus();}
        if (event.key === 'ArrowDown') {event.preventDefault();options[(index+1)%options.length]?.focus();}
        if (event.key === 'ArrowUp') {event.preventDefault();(index<=0 ? search : options[index-1]).focus();}
        if (event.key === 'Enter' && document.activeElement===search) {event.preventDefault();options[0]?.click();}
      });
    }
    element.querySelector('[data-currency-note]').hidden = Object.keys(currencies).every(code=>availableCurrencies.includes(code));
    openButton.addEventListener('click',()=>{
      draft = {language:currentLanguage,currency:currentCurrency};
      updateDraft();closeLists();element.querySelector('[data-locale-error]').hidden=true;
      dialog.showModal();
    });
    element.querySelectorAll('[data-locale-close]').forEach(button=>button.addEventListener('click',()=>dialog.close()));
    dialog.addEventListener('close',()=>{closeLists();openButton.focus();});
    dialog.addEventListener('click',event=>{
      if (event.target===dialog) {const box=dialog.getBoundingClientRect();if(event.clientX<box.left||event.clientX>box.right||event.clientY<box.top||event.clientY>box.bottom)dialog.close();}
      if (!event.target.closest('[data-select]')) closeLists();
    });
    saveButton.addEventListener('click',async()=>{
      saveButton.disabled=true;element.querySelector('[data-locale-error]').hidden=true;
      try {
        await applyLanguage(draft.language,element);
        storage.set('divine-language',draft.language);
        // Only published Shopify languages are submitted. The theme dictionary still
        // provides all twelve interface languages when a market has fewer languages.
        const publishedLanguage = nativeLanguages.find(code=>code===draft.language || code.split('-')[0]===draft.language) || nativeLanguage;
        const chosenCountry = markets.find(m=>m.country===element.dataset.country && m.currency===draft.currency)?.country || markets.find(m=>m.currency===draft.currency)?.country;
        if (!chosenCountry) throw new Error('Currency is not enabled by this store');
        if (!preview && (publishedLanguage!==nativeLanguage || chosenCountry!==element.dataset.country)) {
          const form = element.querySelector('[data-locale-form] form');
          form.elements.language_code.value=publishedLanguage;
          form.elements.country_code.value=chosenCountry;
          form.submit();
          return;
        }
        currentLanguage=draft.language;currentCurrency=draft.currency;
        if(preview) storage.set('divine-currency',currentCurrency);
        updateHeader();dialog.close();
      } catch {element.querySelector('[data-locale-error]').hidden=false;}
      finally {saveButton.disabled=false;}
    });
    updateHeader();updateDraft();
    applyLanguage(currentLanguage,element).catch(()=>{currentLanguage=nativeLanguage.split('-')[0];if(!languages[currentLanguage])currentLanguage='en';updateHeader();});
  }
  observer = new MutationObserver(records=>{
    if (activeLanguage==='en') return;
    // Coalesce updates from carousels, section rendering and product filters.
    if (records.some(record=>record.type==='childList'||record.type==='characterData')) translateDOM();
  });
  document.querySelectorAll('[data-localization]').forEach(initialize);
  document.addEventListener('shopify:section:load',()=>{document.querySelectorAll('[data-localization]').forEach(initialize);translateDOM();});
})();
